import { useUser } from "@clerk/clerk-expo";
import { Control, Controller } from "react-hook-form";
import { Circle } from "tamagui";

import type { AffiliationStatusResult } from "@/api/generated/models";
import type { UserProfileFormData } from "@/api/mutations/userProfile";
import {
  Grid,
  Input,
  Link,
  Select,
  Separator,
  Text,
  XStack,
  YStack,
  getGridLayout,
} from "@/components";
import {
  GENDER_IDENTITY_OPTIONS,
  PRONOUN_OPTIONS,
  TIMEZONE_OPTIONS,
} from "@/settings/constants";
import type { UserProfileSchemaType } from "../tabs/profile";

export interface LockedProfileProps {
  data: UserProfileFormData | undefined;
  control: Control<UserProfileSchemaType>;
  affiliationStatus?: AffiliationStatusResult;
}

export default function LockedProfile({
  data,
  control,
  affiliationStatus,
}: LockedProfileProps) {
  const { user } = useUser();
  if (!data) return null;

  const dobAndSex = [data.dateOfBirth, data.sexAssignedAtBirth]
    .filter(Boolean)
    .join(" • ");

  return (
    <>
      <YStack
        padding="$300"
        borderRadius="$150"
        outlineWidth={2}
        outlineColor="$strokeNeutral"
        outlineStyle="solid"
        gap="$200"
      >
        <YStack gap="$100">
          {(data.firstName || data.lastName) && (
            <Text fontWeight="bold">
              {[data.firstName, data.lastName].filter(Boolean).join(" ")}
            </Text>
          )}
          {dobAndSex && <Text>{dobAndSex}</Text>}
          {user?.primaryPhoneNumber?.phoneNumber && (
            <Text>{user.primaryPhoneNumber.phoneNumber}</Text>
          )}
        </YStack>
        {affiliationStatus?.partner_name && (
          <>
            <Separator borderColor="$fillNeutral" />
            <XStack alignItems="center" gap="$100">
              <Circle
                size="$100"
                backgroundColor={affiliationStatus.active ? "$green" : "$red"}
                flexShrink={0}
              />
              <Text size="$sm" fontWeight="bold">
                {affiliationStatus.active
                  ? "Membership active: "
                  : "Membership inactive"}
              </Text>
              {affiliationStatus.active && (
                <Text size="$sm">
                  Provided by {affiliationStatus.partner_name}
                </Text>
              )}
            </XStack>
          </>
        )}
      </YStack>
      <Text
        marginTop="$200"
        alignSelf="center"
        size="$sm"
        fontStyle="italic"
        color="$textSecondary"
        textAlign="center"
        display="flex"
        flexDirection="column"
        $lg={{
          flexDirection: "row",
        }}
      >
        Need to make a change to your information?{" "}
        <Link
          size="$sm"
          bold
          href="https://www.curaihealth.com/support"
          external
        >
          Contact support
        </Link>
      </Text>
      <Separator borderColor="$fillNeutral" marginVertical="$200" />

      <Grid>
        <Controller
          control={control}
          name="email"
          render={({
            field: { onChange, onBlur, ref, value },
            fieldState: { error },
          }) => (
            <Input.Controlled
              ref={ref}
              label="Email address"
              value={value ?? undefined}
              placeholder="Email address"
              errorText={error?.message}
              onChangeText={onChange}
              onBlur={onBlur}
              rootProps={{
                ...getGridLayout(6),
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="preferredName"
          render={({ field: { onChange, onBlur, ref, value } }) => (
            <Input
              ref={ref}
              label="Preferred name"
              placeholder="Preferred name"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              rootProps={{
                ...getGridLayout(6),
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="genderIdentity"
          render={({ field: { onChange, ref, value } }) => (
            <Select
              ref={ref}
              label="Gender identity"
              placeholder="Select option"
              items={GENDER_IDENTITY_OPTIONS}
              value={value}
              onValueChange={onChange}
              {...getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="pronouns"
          render={({ field: { onChange, ref, value } }) => (
            <Select
              ref={ref}
              label="Pronouns"
              placeholder="Select option"
              items={PRONOUN_OPTIONS}
              value={value}
              onValueChange={onChange}
              {...getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="timezone"
          render={({ field: { onChange, ref, value } }) => (
            <Select
              ref={ref}
              label="Timezone"
              items={TIMEZONE_OPTIONS}
              value={value}
              onValueChange={onChange}
              {...getGridLayout(6)}
            />
          )}
        />
      </Grid>
    </>
  );
}
